import React, { useState, useEffect } from 'react';

const API_URL = window.location.hostname === 'localhost' || window.location.hostname.endsWith('.localhost') || window.location.hostname === '127.0.0.1'
  ? 'http://localhost:5000'
  : (process.env.REACT_APP_API_URL || 'https://dineflowbackend.onrender.com');

const DevDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [masterKey, setMasterKey] = useState('');
  const [, setDevToken] = useState('');
  const [companies, setCompanies] = useState([]);
  const [leads, setLeads] = useState([]);
  const [feedbacks, setFeedbacks] = useState([]);
  const [activeTab, setActiveTab] = useState('companies');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Announcement Banner settings
  const [bannerActive, setBannerActive] = useState(false);
  const [bannerText, setBannerText] = useState('');
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsMessage, setSettingsMessage] = useState('');

  useEffect(() => {
    const savedToken = localStorage.getItem('dev_token');
    const savedKey = localStorage.getItem('master_dev_key');
    if (savedToken && savedKey) {
      setDevToken(savedToken);
      setMasterKey(savedKey);
      setIsLoggedIn(true);
      fetchAllData(savedToken);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchAllData = async (token) => {
    fetchCompanies(token);
    fetchLeads(token);
    fetchFeedbacks(token);
    fetchBannerSettings();
  };

  const fetchBannerSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/public/announcement-banner`);
      const json = await res.json();
      if (json.success && json.data) {
        setBannerActive(json.data.is_active);
        setBannerText(json.data.banner_text);
      }
    } catch (err) {
      console.error('Failed to fetch banner settings:', err);
    }
  };

  const saveBannerSettings = async () => {
    setSettingsLoading(true);
    setSettingsMessage('');
    setError('');
    try {
      const token = localStorage.getItem('dev_token');
      const res = await fetch(`${API_URL}/api/dev/announcement-banner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: bannerActive, banner_text: bannerText })
      });
      const json = await res.json();
      if (json.success) {
        setSettingsMessage('Settings saved successfully!');
      } else {
        setError(json.message || 'Failed to save settings');
      }
    } catch (err) {
      setError('Connection failed');
    } finally {
      setSettingsLoading(false);
    }
  };

  const fetchCompanies = async (token) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/dev/companies`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) {
        setCompanies(json.data);
      } else {
        setIsLoggedIn(false);
        localStorage.removeItem('dev_token');
      }
    } catch (err) {
      setError('Failed to fetch companies');
    } finally {
      setLoading(false);
    }
  };

  const fetchLeads = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/dev/leads`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) setLeads(json.leads || []);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchFeedbacks = async (token) => {
    try {
      const res = await fetch(`${API_URL}/api/dev/feedbacks`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const json = await res.json();
      if (json.success) setFeedbacks(json.feedbacks || []);
    } catch (err) {
      console.error(err);
    }
  };

  const handleDevLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/auth/dev-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ masterKey })
      });
      const json = await res.json();
      if (json.success) {
        setDevToken(json.token);
        setIsLoggedIn(true);
        localStorage.setItem('dev_token', json.token);
        localStorage.setItem('master_dev_key', masterKey);
        fetchAllData(json.token);
      } else {
        setError('Invalid Master Key');
      }
    } catch (err) {
      setError('Connection failed');
    } finally {
      setLoading(false);
    }
  };

  const impersonateUser = async (email) => {
    if (!window.confirm(`Login as ${email}?`)) return;
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/impersonate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, masterKey })
      });
      const json = await res.json();
      
      if (json.success) {
        // Standard auth storage
        localStorage.setItem('token', json.token);
        localStorage.setItem('user', JSON.stringify(json.user));
        localStorage.setItem('company', JSON.stringify(json.company));
        
        // Determine redirect URL
        const hostname = window.location.hostname;
        const protocol = window.location.protocol;
        
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          // Stay on localhost, just go to homepage
          window.location.href = `/homepage?token=${json.token}`;
        } else {
          // Construct subdomain URL - Local storage isn't shared across subdomains, 
          // so we MUST pass the token in the URL for the subdomain to pick it up.
          const baseDomain = hostname.split('.').slice(-2).join('.');
          window.location.href = `${protocol}//${json.company.slug}.${baseDomain}/homepage?token=${json.token}`;
        }
      } else {
        alert('Impersonation failed: ' + json.message);
      }
    } catch (err) {
      alert('Impersonation error');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('dev_token');
    localStorage.removeItem('master_dev_key');
    setIsLoggedIn(false);
    setCompanies([]);
  };

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 p-8 rounded-2xl shadow-2xl w-full max-w-md border border-slate-700">
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-emerald-500/10 text-emerald-500 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-emerald-500/20">
              <i className="fas fa-user-shield text-2xl"></i>
            </div>
            <h2 className="text-2xl font-black text-white">Developer Admin</h2>
            <p className="text-slate-400 text-sm">Enter Master Key to access dev dashboard</p>
          </div>
          
          <form onSubmit={handleDevLogin} className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-2">Master Dev Key</label>
              <input 
                type="password" 
                required 
                className="w-full bg-slate-900 border border-slate-700 text-white p-3 rounded-xl focus:ring-2 focus:ring-emerald-500 outline-none transition-all"
                value={masterKey}
                onChange={(e) => setMasterKey(e.target.value)}
                placeholder="••••••••••••••••"
              />
            </div>
            {error && <p className="text-rose-500 text-xs font-bold bg-rose-500/10 p-2 rounded-lg border border-rose-500/20 text-center">{error}</p>}
            <button 
              type="submit" 
              disabled={loading}
              className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black py-3 rounded-xl transition-all shadow-lg shadow-emerald-900/20 disabled:opacity-50"
            >
              {loading ? <i className="fas fa-spinner animate-spin mr-2"></i> : null}
              ACCESS DASHBOARD
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-emerald-600 text-white rounded-xl flex items-center justify-center shadow-lg shadow-emerald-200">
              <i className="fas fa-shield-alt"></i>
            </div>
            <div>
              <h1 className="text-lg font-black text-gray-900 tracking-tight uppercase">Dev Dashboard</h1>
              <p className="text-[10px] text-gray-500 font-bold uppercase tracking-widest leading-none">Superadmin Management</p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="hidden md:block text-right mr-2">
              <p className="text-xs font-bold text-gray-900 leading-none">Developer Mode</p>
              <p className="text-[10px] text-emerald-600 font-black uppercase mt-1">Authenticated</p>
            </div>
            <button 
              onClick={handleLogout}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-4 py-2 rounded-xl text-xs font-black transition-all border border-gray-200"
            >
              LOGOUT
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Summary */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">Total Clients</p>
            <h3 className="text-3xl font-black text-gray-900">{companies.length}</h3>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">System Health</p>
            <h3 className="text-3xl font-black text-emerald-600">Operational</h3>
          </div>
          <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100">
            <p className="text-gray-500 text-[10px] font-black uppercase tracking-widest mb-1">Master Key</p>
            <h3 className="text-3xl font-black text-gray-900">Active</h3>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-4 mb-6 border-b border-gray-200">
          <button 
            className={`pb-4 px-2 font-bold text-sm ${activeTab === 'companies' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('companies')}
          >
            Registered Companies ({companies.length})
          </button>
          <button 
            className={`pb-4 px-2 font-bold text-sm ${activeTab === 'leads' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('leads')}
          >
            Platform Leads ({leads.length})
          </button>
          <button 
            className={`pb-4 px-2 font-bold text-sm ${activeTab === 'feedbacks' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('feedbacks')}
          >
            User Feedbacks ({feedbacks.length})
          </button>
          <button 
            className={`pb-4 px-2 font-bold text-sm ${activeTab === 'settings' ? 'border-b-2 border-emerald-600 text-emerald-600' : 'text-gray-500 hover:text-gray-700'}`}
            onClick={() => {
              setActiveTab('settings');
              fetchBannerSettings();
            }}
          >
            Management Settings
          </button>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <h2 className="text-lg font-black text-gray-900">
              {activeTab === 'companies' && 'Registered Companies'}
              {activeTab === 'leads' && 'Platform Leads'}
              {activeTab === 'feedbacks' && 'User Feedbacks'}
              {activeTab === 'settings' && 'Global Announcement Settings'}
            </h2>
            {activeTab !== 'settings' && (
              <div className="relative">
                 <input 
                  type="text" 
                  placeholder="Search by name or email..." 
                  className="pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-emerald-500 outline-none w-64 transition-all"
                 />
                 <i className="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              </div>
            )}
          </div>
          
          {activeTab === 'settings' ? (
            <div className="p-8 max-w-2xl">
              <div className="space-y-6">
                <div>
                  <h3 className="text-sm font-bold text-gray-700 uppercase tracking-wider mb-2">Global Homepage Announcement Banner</h3>
                  <p className="text-xs text-gray-500 mb-4">This banner is displayed at the top of the Homepage for all logged-in restaurant owners and staff dashboards.</p>
                </div>

                <div className="flex items-center gap-3 bg-slate-50 p-4 rounded-xl border border-slate-100">
                  <input
                    type="checkbox"
                    id="bannerActive"
                    className="w-5 h-5 accent-emerald-600 rounded cursor-pointer"
                    checked={bannerActive}
                    onChange={(e) => setBannerActive(e.target.checked)}
                  />
                  <label htmlFor="bannerActive" className="text-sm font-bold text-gray-900 cursor-pointer select-none">
                    Enable Banner (Show Announcement)
                  </label>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-600 uppercase tracking-wide mb-2">Banner Message Text</label>
                  <textarea
                    rows="3"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm focus:ring-2 focus:ring-emerald-500 outline-none text-slate-800 font-medium"
                    placeholder="Enter announcement text to display to all dashboard users..."
                    value={bannerText}
                    onChange={(e) => setBannerText(e.target.value)}
                    disabled={!bannerActive}
                  />
                </div>

                {settingsMessage && (
                  <div className="bg-emerald-50 text-emerald-700 p-3 rounded-xl border border-emerald-100 text-xs font-bold flex items-center gap-2">
                    <i className="fas fa-check-circle"></i> {settingsMessage}
                  </div>
                )}
                {error && (
                  <div className="bg-rose-50 text-rose-700 p-3 rounded-xl border border-rose-100 text-xs font-bold flex items-center gap-2">
                    <i className="fas fa-exclamation-circle"></i> {error}
                  </div>
                )}

                <button
                  type="button"
                  disabled={settingsLoading}
                  onClick={saveBannerSettings}
                  className="bg-emerald-600 hover:bg-emerald-500 text-white font-black px-6 py-3 rounded-xl text-xs uppercase tracking-wider transition-all disabled:opacity-50"
                >
                  {settingsLoading ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  {activeTab === 'companies' && (
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">ID</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Company Name</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Slug / Subdomain</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Primary Admin</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Registration Date</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500 text-right">Actions</th>
                    </tr>
                  )}
                  {activeTab === 'leads' && (
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">ID</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Name</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Phone</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Email</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Date</th>
                    </tr>
                  )}
                  {activeTab === 'feedbacks' && (
                    <tr className="bg-gray-50 border-b border-gray-100">
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">ID</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Category</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Rating</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Comments</th>
                      <th className="px-6 py-4 text-[10px] font-black uppercase tracking-widest text-gray-500">Date</th>
                    </tr>
                  )}
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="px-6 py-12 text-center text-gray-400 font-bold">
                        <i className="fas fa-circle-notch animate-spin mr-2"></i> Loading data...
                      </td>
                    </tr>
                  ) : activeTab === 'companies' ? (
                    companies.length === 0 ? (
                      <tr>
                        <td colSpan="6" className="px-6 py-12 text-center text-gray-400 font-bold">No companies found.</td>
                      </tr>
                    ) : (
                      companies.map((company) => (
                        <tr key={company.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-xs font-bold text-gray-400">#{company.id}</td>
                          <td className="px-6 py-4 font-black text-gray-900">{company.name}</td>
                          <td className="px-6 py-4">
                            <span className="bg-emerald-50 text-emerald-700 px-2 py-1 rounded-lg text-xs font-black uppercase tracking-tight border border-emerald-100">
                              {company.slug}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex flex-col">
                              <span className="text-sm font-bold text-gray-900">{company.admin_email}</span>
                              <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Primary Owner</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-xs text-gray-500 font-medium">
                            {new Date(company.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 text-right">
                            <button 
                              onClick={() => {
                                const email = window.prompt("Enter the email ID to login as:", company.admin_email);
                                if (email) impersonateUser(email);
                              }}
                              className="bg-emerald-50 hover:bg-emerald-100 text-emerald-700 px-3 py-1 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all"
                            >
                              Login As
                            </button>
                          </td>
                        </tr>
                      ))
                    )
                  ) : activeTab === 'leads' ? (
                    leads.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-gray-400 font-bold">No leads found.</td>
                      </tr>
                    ) : (
                      leads.map((lead) => (
                        <tr key={lead.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-xs font-bold text-gray-400">#{lead.id}</td>
                          <td className="px-6 py-4 font-black text-gray-900">{lead.name}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{lead.phone}</td>
                          <td className="px-6 py-4 text-sm text-gray-700">{lead.email || '-'}</td>
                          <td className="px-6 py-4 text-xs text-gray-500 font-medium">
                            {new Date(lead.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )
                  ) : activeTab === 'feedbacks' ? (
                    feedbacks.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-12 text-center text-gray-400 font-bold">No feedbacks found.</td>
                      </tr>
                    ) : (
                      feedbacks.map((fb) => (
                        <tr key={fb.id} className="hover:bg-gray-50 transition-colors">
                          <td className="px-6 py-4 text-xs font-bold text-gray-400">#{fb.id}</td>
                          <td className="px-6 py-4">
                            <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-lg text-xs font-black uppercase tracking-tight border border-blue-100">
                              {fb.category}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex text-amber-400">
                              {[1,2,3,4,5].map(star => (
                                <i key={star} className={`fas fa-star ${fb.rating >= star ? '' : 'text-gray-300'}`}></i>
                              ))}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-700 max-w-xs truncate" title={fb.comments}>{fb.comments || '-'}</td>
                          <td className="px-6 py-4 text-xs text-gray-500 font-medium">
                            {new Date(fb.created_at).toLocaleString()}
                          </td>
                        </tr>
                      ))
                    )
                  ) : null}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default DevDashboard;
